import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocalStorage, useSessionStorage } from 'react-use';

import { Badge, Button, Checkbox, ContentLayout, Header, SpaceBetween, Table } from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';

import { archiveBatches, batchFromName, fetchBatches, subscribeToBatches } from '../api/batches';
import { fetchItemsWithFilter, Filter } from '../api/generic';
import { getLatestGravitySample } from '../api/sample';
import DisplayImage from '../components/DisplayImage';
import { Space } from '../components/Flex';
import { HydroPiSample, iSpindelSample } from '../components/modules/HydroPi';
import PeriodFilterControls, { ViewMode } from '../components/PeriodFilterControls';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch, BatchViewModel, getBatchSlug } from '../models/Batch';
import { GravitySample } from '../models/Sampling';
import { costPerUnit } from '../utils/cost';
import { daysUntilDate, getEndOfQuarter, getEndOfYear, getStartOfQuarter, getStartOfYear } from '../utils/date';
import {
  checkFermentationStatus,
  checkFermentationStatusSimple,
  FermentationStatus,
  FermentationStatusEmojis,
} from '../utils/hydroPi';
import { prefixCollection } from '../utils/prefix';
import { calculateABV, calculateABVSimple, calculateDateDay, calculateFinalABV } from '../utils/steps';
import { addDaysBySteps, toDate } from '../utils/timestamp';
import { renderStars } from './BatchPage';

const Batches = () => {
  const alert = useAlertContext();
  const { tenant, department, currency } = useAuthContext();
  const navigate = useNavigate();
  const nowTs = Timestamp.now().seconds;
  const [batches, setBatches] = useState<Batch[]>([]);

  const [batchHydroPiData, setBatchHydroPiData] = useState<{
    [batchId: string]: HydroPiSample[];
  }>({});

  const [sampleRecords, setSampleRecords] = useState<{ sample: GravitySample; id: string }[]>([]);
  const [batchesViewModel, setBatchesViewModel] = useState<BatchViewModel[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<BatchViewModel[]>([]);
  const [batchSlugs, setBatchSlugs] = useState<Map<string, string>>(new Map());

  const [isDescending, setIsDescending] = useSessionStorage('isDescending_batch', false);
  const [sortingColumn, setSortingColumn] = useSessionStorage<keyof Batch>('sortingColumn_batch', 'batchId');

  const [showArchived, setShowArchived] = useLocalStorage('showArchived', false);
  const [archivingMode, setArchivingMode] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());

  const [viewMode, setViewMode] = useLocalStorage<ViewMode>('batch_period_view', 'quarter');
  const [activeDate, setActiveDate] = useState<Date>(new Date());

  const handlePeriodChange = (direction: 'prev' | 'next') => {
    setActiveDate((prev) => {
      const month = prev.getMonth();
      const year = prev.getFullYear();

      if (viewMode === 'quarter') {
        return new Date(year, direction === 'prev' ? month - 3 : month + 3, 1);
      } else if (viewMode === 'month') {
        return new Date(year, direction === 'prev' ? month - 1 : month + 1, 1);
      } else {
        return new Date(direction === 'prev' ? year - 1 : year + 1, 0, 1);
      }
    });
  };

  const toggleArchivingMode = () => {
    setArchivingMode((prev) => !prev);
    setSelectedBatchIds(new Set()); // Clear selection when toggling mode
  };

  const handleSelectBatch = (batchId: string, selected: boolean) => {
    setSelectedBatchIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(batchId);
      } else {
        newSet.delete(batchId);
      }
      return newSet;
    });
  };

  const handleArchiveBatches = async () => {
    if (selectedBatchIds.size === 0) {
      alert.warn('No batches selected for archiving.');
      return;
    }

    const confirmArchiving = window.confirm(
      `Are you sure you want to archive ${selectedBatchIds.size} batch(es)? This action cannot be undone.`,
    );

    if (!confirmArchiving) {
      return; // Exit if the user cancels the action
    }

    try {
      await archiveBatches(Array.from(selectedBatchIds), tenant);
      alert.success('Batches archived successfully!');
      setSelectedBatchIds(new Set());
      setArchivingMode(false);
    } catch (error) {
      console.error('Failed to archive batches:', error);
      alert.error('Failed to archive batches. Please try again.');
    }
  };

  function fixInvalidEndDate(expectedEndDate: Date): Date {
    if (isNaN(expectedEndDate.getTime()) || expectedEndDate.getFullYear() < 2000) {
      const now = new Date();
      now.setDate(now.getDate() + 90);
      return now;
    }
    return expectedEndDate;
  }

  useEffect(() => {
    const unsubscribeBatches = () => subscribeToBatches(setBatches, tenant);

    return () => {
      unsubscribeBatches();
    };
  }, [tenant]);

  useEffect(() => {
    fetchBatches(tenant, sortingColumn, !isDescending).then(setBatches);
  }, [tenant, isDescending, sortingColumn]);

  useEffect(() => {
    const fetchSamples = async () => {
      const batchesWithId = await Promise.all(
        batches.map(async (batch) => {
          const sample = await getLatestGravitySample(batch.id, tenant);
          return { sample, id: batch.id };
        }),
      );
      setSampleRecords(batchesWithId);
    };
    fetchSamples();
  }, [batches, tenant]);

  // Fetch batch slugs
  useEffect(() => {
    const fetchBatchSlugs = async () => {
      const slugs = await Promise.all(batches.map((batch) => getBatchSlug(batch, tenant)));
      setBatchSlugs(new Map(slugs.map((slug, index) => [batches[index].id, slug])));
    };
    fetchBatchSlugs();
  }, [batches, tenant]);

  // Calculate view model
  useEffect(() => {
    const viewModel = batches.map((batch) => {
      const sampleRecord = sampleRecords.find((s) => s.id === batch.id);
      const sample = sampleRecord ? sampleRecord.sample : null;
      let abvNow = sample ? calculateABV(sample) + '%' : '';
      let finalAbv = sample ? calculateFinalABV(sample) + '%' : '';
      let status: FermentationStatus = checkFermentationStatusSimple({
        sampleRecords,
        batchId: batch.id,
        gravityThreshold: 1.0, // Typical final gravity
        stalledThreshold: 3, // 3 days without updates
      });

      if (batch.hydroPi && batch.batchId && batchHydroPiData[batch.id]) {
        const batchHydroPiDataFiltered = batchHydroPiData[batch.id];

        const sortedPies = [...batchHydroPiDataFiltered].sort((a, b) => a.timestamp.seconds - b.timestamp.seconds);
        const og = sortedPies[0]?.avg_gravity ?? 0;
        const sg = sortedPies[sortedPies.length - 1]?.avg_gravity ?? 0;
        // console.log(og, sg);
        abvNow = calculateABVSimple(og, sg) + '%';
        finalAbv = calculateABVSimple(og, 1) + '%';
        status = checkFermentationStatus({
          samples: batchHydroPiData[batch.id] || [],
          startDate: batch.hydroPi.startDate,
          endDate: batch.hydroPi.endDate,
          gravityThreshold: 1.0, // Typical value for finished gravity
          stalledThreshold: 3, // 3 days with no progress
        });
      }

      const currentStepName = batch.recipe?.steps[batch.currentStepIndex]?.name ?? 'N/A';

      let nextActionDate = 'N/A';
      let ndate = batch.recipe?.steps[batch.currentStepIndex]?.end
        ? toDate(batch.recipe?.steps[batch.currentStepIndex].end ?? new Date())
        : null;
      // if date is null, calculate it from the current step duration
      if (!ndate) {
        console.log(batch);
        ndate = addDaysBySteps(
          toDate(batch?.startDate),
          (batch?.recipe?.steps ?? [])?.slice(0, (batch.currentStepIndex ?? 0) + 1),
        );
      }
      nextActionDate = daysUntilDate(ndate).toFixed(0) !== '-1' ? daysUntilDate(ndate).toFixed(0) + 'd' : '✅';

      const date = sample ? toDate(sample.date) : new Date();
      const progress = calculateDateDay(batch, date).progress;
      const costL = costPerUnit(batch).toFixed(2);
      const slug = batchSlugs.get(batch.id) || batch.recipeSlug || '';

      return {
        ...batch,
        abvNow,
        finalAbv,
        currentStepName,
        progress,
        costL,
        nextActionDate,
        fStatus: status,
        slug,
      };
    });
    setBatchesViewModel(viewModel);
  }, [batches, sampleRecords, tenant, nowTs, batchHydroPiData, batchSlugs]);

  useEffect(() => {
    const fetchBatchHydroPiData = async () => {
      if (!batches || batches.length === 0) return;

      // Filter batches to include only those with startDate and no endDate
      const batchDataPromises = batches
        .filter(
          (batch) =>
            batch.status === 'in-progress' && batch.hydroPi?.type === 'Tilt' && !!batch.startDate && !batch.endDate,
        )
        .map(async (batch) => {
          if (batch.hydroPi) {
            const startDate = batch.hydroPi.startDate.seconds;
            const endDate = batch.hydroPi.endDate?.seconds ?? nowTs;

            const filters: Filter<HydroPiSample>[] = [
              {
                field: 'color',
                operator: '==',
                value: batch.hydroPi.color,
              },
              {
                field: 'timestamp',
                operator: '>=',
                value: Timestamp.fromMillis(startDate * 1000),
              },
              {
                field: 'timestamp',
                operator: '<=',
                value: Timestamp.fromMillis(endDate * 1000),
              },
            ];

            const hydroPiSamples = await fetchItemsWithFilter<HydroPiSample>(
              prefixCollection('hydroPi', tenant, '-'),
              filters,
            );

            return { batchId: batch.id, hydroPiSamples };
          }
          return null;
        });

      const batchDataPromisesISpindel = batches
        .filter(
          (batch) =>
            batch.status === 'in-progress' && batch.hydroPi?.type === 'iSpindel' && !!batch.startDate && !batch.endDate,
        )
        .map(async (batch) => {
          if (batch.hydroPi) {
            const startDate = batch.hydroPi.startDate.seconds;
            const endDate = batch.hydroPi.endDate?.seconds ?? nowTs;
            // console.log(batch.hydroPi.color);
            const filters: Filter<iSpindelSample>[] = [
              {
                field: 'color',
                operator: '==',
                value: batch.hydroPi.color,
              },
              {
                field: 'timestamp',
                operator: '>=',
                value: Timestamp.fromMillis(startDate * 1000),
              },
              {
                field: 'timestamp',
                operator: '<=',
                value: Timestamp.fromMillis(endDate * 1000),
              },
            ];

            const hydroPiSamples = await fetchItemsWithFilter<iSpindelSample>(
              prefixCollection('ispindel', tenant, '-'),
              filters,
            );

            return {
              batchId: batch.id,
              hydroPiSamples: hydroPiSamples.map((hps) => {
                return {
                  timestamp: hps.timestamp,
                  avg_gravity: hps.gravity,
                } as HydroPiSample;
              }),
            };
          }
          return null;
        });

      // Wait for all batch data to be fetched
      const batchHydroPiResults = await Promise.all([...batchDataPromises, ...batchDataPromisesISpindel]);

      // console.log(batchHydroPiResults);

      // Transform results into a key-value structure
      const hydroPiDataMap = batchHydroPiResults.reduce(
        (acc, result) => {
          if (result) {
            acc[result.batchId] = result.hydroPiSamples;
          }
          return acc;
        },
        {} as { [batchId: string]: HydroPiSample[] },
      );

      setBatchHydroPiData(hydroPiDataMap);
    };

    fetchBatchHydroPiData();
  }, [batches, tenant]);

  const columnDefinitions = [
    archivingMode
      ? {
          id: 'select',
          header: '',
          cell: (item: BatchViewModel) => (
            <Checkbox
              checked={selectedBatchIds.has(item.id)}
              onChange={(e) => handleSelectBatch(item.id, e.detail.checked)}
            />
          ),
          width: 50,
        }
      : {
          id: 'select',
          header: 'hide',
          cell: () => '',
        },
    {
      header: 'Batch',
      cell: (item: BatchViewModel) => <strong>{item.batchId || batchFromName(item.name) || ''}</strong>,
      isRowHeader: true,
      sortingField: 'batchId',
    },
    {
      header: 'Image',
      cell: (item: BatchViewModel) => (
        <DisplayImage
          fileName="image"
          filePath={'recipes/' + item.recipeId}
          round
          altText="Recipe image"
          style={{ height: 50, width: 50 }}
        />
      ),
    },
    {
      header: 'Name',
      cell: (item: BatchViewModel) => (
        <Link to={`/batches/${item.id}`}>
          <strong>{item.name ?? 'N/A'}</strong>
        </Link>
      ),
      isRowHeader: true,
      sortingField: 'name',
    },
    {
      header: 'Status',
      cell: (item: BatchViewModel) => (
        <Badge color={item.status === 'in-progress' ? 'blue' : item.status === 'completed' ? 'green' : 'grey'}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {((item?.status ?? 'planned').charAt(0).toUpperCase() + (item?.status ?? 'planned').slice(1)).replace(
              '-',
              ' ',
            )}
          </span>
        </Badge>
      ),
    },
    {
      header: 'Ferm.',
      cell: (item: BatchViewModel) => {
        return (
          <p style={{ whiteSpace: 'nowrap' }}>
            {item.fStatus ? FermentationStatusEmojis[item.fStatus] : ''} {item.fStatus}
          </p>
        );
      },
    },
    {
      header: 'ABV (now)',
      cell: (item: BatchViewModel) => <>{item.abvNow}</>,
    },
    {
      header: 'ABV',
      cell: (item: BatchViewModel) => <>{item.finalAbv}</>,
    },
    {
      header: 'Next action',
      cell: (item: BatchViewModel) => (
        <>{item.nextActionDate}</> // Directly use the pre-computed nextActionDate
      ),
    },
    {
      header: 'Step',
      cell: (item: BatchViewModel) => <>{item.currentStepName}</>,
      sortingField: 'currentStepIndex',
    },
    {
      header: 'Score',

      cell: (item: BatchViewModel) => ((item.score ?? -1) > 0 ? renderStars(item.score ?? 0) : ''),
      minWidth: 120,
    },
    {
      header: 'Recipe',
      cell: (item: BatchViewModel) => <>{item.recipe?.name ?? 'N/A'}</>,
    },
    {
      header: 'Quantity',
      cell: (item: BatchViewModel) => <>{item.quantity ?? 'N/A'}</>,
    },
    {
      header: 'Cost L',
      cell: (item: BatchViewModel) => <span style={{ whiteSpace: 'nowrap' }}>{currency(item.costL)}</span>,
    },
    {
      header: 'Started',
      cell: (item: BatchViewModel) => {
        const date = toDate(item.startDate);
        return <>{date?.toLocaleDateString() ?? ''}</>;
      },
    },
    {
      header: 'Completed',
      cell: (item: BatchViewModel) => <>{item?.endDate ? toDate(item.endDate)?.toLocaleDateString() : ''}</>,
    },
    {
      header: 'Slug',
      cell: (item: BatchViewModel) => <>{batchSlugs.get(item.id) ?? ''}</>,
    },
  ];

  const getPeriodBounds = (): [Date, Date] => {
    if (viewMode === 'month') {
      const start = new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
      const end = new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0);
      return [start, end];
    } else if (viewMode === 'quarter') {
      return [getStartOfQuarter(activeDate), getEndOfQuarter(activeDate)];
    } else {
      return [getStartOfYear(activeDate), getEndOfYear(activeDate)];
    }
  };

  useEffect(() => {
    const [start, end] = getPeriodBounds();

    const filtered = batchesViewModel.filter((x) => {
      const belongsToDepartment =
        department === '__not_set' ? !x.department : x.department === department || department === '';

      const startDate = toDate(x.startDate);
      let expectedEndDate = x.endDate
        ? toDate(x.endDate)
        : x.startDate && x.recipe?.steps
          ? addDaysBySteps(toDate(x.startDate), x.recipe.steps)
          : new Date();

      expectedEndDate = fixInvalidEndDate(expectedEndDate);

      const overlapsPeriod = startDate <= end && expectedEndDate >= start;

      return belongsToDepartment && overlapsPeriod;
    });

    setFilteredBatches(filtered);
  }, [batchesViewModel, department, viewMode, activeDate]);

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          description={'Hint: Have the recepies and ingredients ready'}
          actions={
            <SpaceBetween size="m" direction="horizontal">
              {archivingMode ? (
                <>
                  <Button iconName="folder" variant="primary" onClick={handleArchiveBatches}>
                    Archive Selected
                  </Button>
                  <Button iconName="close" variant="normal" onClick={toggleArchivingMode}>
                    Cancel Archiving
                  </Button>
                </>
              ) : (
                <>
                  <Button iconName="add-plus" variant="primary" onClick={() => navigate('/batches/new')}>
                    Start a batch
                  </Button>
                  <Button iconName="folder" variant="primary" onClick={toggleArchivingMode}>
                    Archive Batches
                  </Button>
                </>
              )}
              {showArchived ? (
                <Button variant="primary" iconName="check" onClick={() => setShowArchived(false)}>
                  Hide archived
                </Button>
              ) : (
                <Button onClick={() => setShowArchived(true)}>Show archived</Button>
              )}
            </SpaceBetween>
          }
        >
          Batches
        </Header>
      }
    >
      <div style={{ marginTop: 10 }}>
        <PeriodFilterControls
          viewMode={viewMode ?? 'quarter'}
          setViewMode={setViewMode}
          activeDate={activeDate}
          handlePeriodChange={handlePeriodChange}
        />
        <Space h={10} />
        <Table
          wrapLines
          stickyHeader
          variant="embedded"
          items={showArchived ? filteredBatches : filteredBatches.filter((b) => b.status !== 'archived')}
          onSortingChange={({ detail }) => {
            setIsDescending(detail?.isDescending ?? false);
            setSortingColumn((detail?.sortingColumn.sortingField as keyof Batch) ?? 'batchId');
          }}
          sortingDescending={isDescending}
          sortingColumn={columnDefinitions.find((cd) => cd.sortingField === sortingColumn)}
          columnDefinitions={columnDefinitions.filter((x) => x.header !== 'hide')}
        />
      </div>
    </ContentLayout>
  );
};

export default Batches;
